import assert from 'minimalistic-assert';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import AsSeenInWidget from '../components/AsSeenInWidget';
import { Container, PrimaryButton, Section } from '../components/Base';
import Benefits from '../components/Benefits';
import Grid from '../components/Grid';
import { FPSCFreeInfoCard, PremiumInfoCard } from '../components/InfoCard';
import Review from '../components/Review';
import Video from '../components/Video';
import { Stars } from '../components/icons/Star';
import {
  BROWSER_NAMES,
  CAPTURE_VIDEO,
  CURRENT_APP,
  OTHER_APPS,
  STATS,
} from '../constants';
import connectExtension, { EXTENSION_PROPS } from '../extension/Provider';
import { getTitle } from '../utils/document';
import { tr } from '../utils/e7n-util';
import './HomePage.scss';
import page from './HomePageData.ts';

import clsx from 'clsx';
import editorHp from '../../assets/img/hp-big-580.png';
import editorHp2x from '../../assets/img/hp-big-580@2x.png';
import screenshotHp from '../../assets/img/hp-small-290.png';
import screenshotHp2x from '../../assets/img/hp-small-290@2x.png';
import { AndList } from '../components/AndList';

assert(CAPTURE_VIDEO, 'CAPTURE_VIDEO is not defined');

const HomePage = ({ extensionIsInstalled, extensionOrigins }) => {
  const benefits = page.benefits;

  _substituteStats(benefits);

  // HACK - do smart line-spacing of title for english
  const rawTitle =
    'The best browser extension for taking a screenshot of an entire webpage';
  let title = tr(rawTitle);
  if (title === rawTitle) {
    title =
      'The best browser extension\n for taking a screenshot\n of an entire webpage';
  }

  return (
    <div className="homepage">
      <Helmet>
        <title>{getTitle()}</title>
      </Helmet>
      <Section className="hero-sec first-sec">
        <Container className="pt-[40px] md:flex md:max-w-[1160px] md:justify-between md:gap-[40px]">
          <div className="mx-auto mb-[40px] md:mb-0 lg:mt-[20px]">
            <h1 className="mb-0 mt-0 !pt-0 text-[60px] font-normal leading-[72px] tracking-[-1.5px] xl:text-[80px] xl:leading-[96px]">
              GoFullPage
            </h1>
            {/* <h1 className="homepage--name">GoFullPage</h1> */}
            <div className="mx-auto mb-[20px] max-w-[315px] text-[20px] leading-[28px] md:mb-[40px] md:min-w-[333px] md:max-w-full xl:text-[22px] xl:leading-[30px]">
              The most reliable full page screenshot{' '}
              <span className="inline md:block">in just a single click</span>
            </div>

            <div className="mb-[16px]">
              <PrimaryButton
                className="btn-standout btn-convert px-[30px]"
                type="link"
                size="medium"
                href={CURRENT_APP.webstore_url}
              >
                Get started (it’s free)
              </PrimaryButton>
            </div>

            <div className="">
              <Stars count={5} alt="Star" /> {STATS.ratings} · Trusted by{' '}
              {STATS.usersLong}
              <br />
              Also available on{' '}
              <AndList
                items={OTHER_APPS.map((app) => ({
                  url: app.webstore_url,
                  text: BROWSER_NAMES[app.browser],
                }))}
              />
            </div>
          </div>
          <HeroImage className="mx-auto w-full shrink md:mx-0 md:max-w-[min(638px,60%)]" />
        </Container>
      </Section>

      {/* <Section className="">
        <Container>
          <div className="mx-auto mb-10 max-w-[680px] text-pretty rounded-lg bg-orange-100 p-5 text-left">
            <div className="p-x-6 w-full space-y-5">
              <div>
                <h3>Chrome Web Store incident</h3>
              </div>
              <div>
                <strong>Sep 10, 2026:</strong> The core Chrome extension is
                available again! You can find it on the{' '}
                <a
                  className="text-orange-600 hover:text-orange-700"
                  href="https://chrome.google.com/webstore/detail/full-page-screen-capture/fdpohaocaechififmbbbbbknoalclacl"
                >
                  Chrome web store
                </a>
                .
              </div>
              <div>
                <strong>Sep 7, 2026:</strong> We have released an official
                version of our extension in the{' '}
                <a
                  className="text-orange-600 hover:text-orange-700"
                  href="https://addons.mozilla.org/en-US/firefox/addon/gofullpage-screenshot/"
                >
                  Firefox Add-ons store
                </a>
                .
              </div>
              <div>
                <strong>Sep 1, 2026:</strong> We have posted additional
                information on our blog on{' '}
                <a
                  className="text-orange-600 hover:text-orange-700"
                  href="https://blog.gofullpage.com/2026/08/14/gofullpage-chrome-update-a-progress-report/"
                >
                  how to keep access to GoFullPage on Chrome
                </a>{' '}
                and our website is temporarily pointing to the GoFullPage BETA
                extension, which can safely be installed separately.
              </div>
              <div>
                <strong>Aug 11, 2026:</strong> There is a temporary issue
                impacting our availability in the Chrome Web Store. GoFullPage
                remains safe and secure. See more{' '}
                <a
                  className="text-orange-600 hover:text-orange-700"
                  href="https://blog.gofullpage.com/2026/08/11/gofullpage-chrome-update/"
                >
                  on our blog
                </a>
                .
              </div>
            </div>
          </div>
        </Container>
      </Section> */}

      <Section className="info-sec">
        <Container>
          <Benefits benefits={benefits} />
        </Container>
      </Section>

      <Section className="blue-sec" bg="link">
        <Container>
          <p>See capturing a full page screenshot in action!</p>
          <Video
            {...CAPTURE_VIDEO}
            preload={'none'}
            autoPlay={false}
            loop={false}
          />
        </Container>
      </Section>

      <AsSeenInWidget />

      <Section className="revs-sec">
        <Container>
          <h2>See what people are saying</h2>
          <p className="subheader">
            Real reviews, from the{' '}
            <a
              className="sly"
              href="https://chrome.google.com/webstore/detail/full-page-screen-capture/fdpohaocaechififmbbbbbknoalclacl?hl=en-US"
            >
              Chrome Web Store extension page
            </a>
            .
          </p>
          <Grid
            cols={2}
            elts={page.reviews.map((r, i) => (
              <Review key={i} {...r} />
            ))}
          />
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="info-cards">
            <FPSCFreeInfoCard
              extensionIsInstalled={extensionIsInstalled}
              extensionOrigins={extensionOrigins}
            />
            <PremiumInfoCard buttonText="Learn more" />
          </div>
        </Container>
      </Section>
    </div>
  );
};

HomePage.propTypes = {
  extensionIsInstalled: PropTypes.bool.isRequired,
  extensionOrigins: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  ...EXTENSION_PROPS,
};

// HACK - convert things like: "With {STATS.ratings} ratings"
// into "With 26K ratings" via the constants.STATS field
const _substituteStats = (benefits) => {
  const rStats = /\{STATS\.([^}]+)\}/g;
  benefits.forEach((benefit) => {
    const sp = benefit.text.split(rStats);
    if (sp.length > 1) {
      benefit.text = sp
        .map((tok, i) => (i % 2 === 1 ? STATS[tok] : tok))
        .join('');
    }
  });
};

export default connectExtension(HomePage);

// ## Helpers

/**
 *
 * @param {import('react').FunctionComponent<{className?: string}>} props
 * @returns
 */
const HeroImage = ({ className }) => {
  return (
    <div className={clsx('relative', className)}>
      {/* w-82% at mobile so we can center on middle of main image */}
      {/* ml-9.09% and max-w-90.9% to create offset for the smaller image */}
      <img
        src={editorHp}
        srcSet={`${editorHp2x} 2x`}
        className="ml-[9.090909%] block aspect-[58/37] w-[82%] max-w-[90.90909%] shadow-md md:w-full"
      />
      <img
        src={screenshotHp}
        srcSet={`${screenshotHp2x} 2x`}
        className="img-w-45 absolute left-0 top-[16%] shadow-lg"
      />
    </div>
  );
};
